import React, { useState, useEffect } from 'react';
import { geocodeAddress, getHourlyForecast, conditionToIcon } from '../../utils/weather';

export default function WeatherForecast({ scheduledAt, address }) {
  const [fc, setFc] = useState(null);

  useEffect(() => {
    if (!scheduledAt || !address) return;
    let alive = true;

    (async () => {
      try {
        const apptTime = new Date(scheduledAt);
        const hoursOut = (apptTime - Date.now()) / 3_600_000;
        if (hoursOut < -1 || hoursOut > 240) return;

        const { lat, lng } = await geocodeAddress(address);
        if (!alive) return;

        const hours = await getHourlyForecast(lat, lng);
        if (!alive || !hours.length) return;

        // Match appointment to nearest hourly slot (UTC comparison)
        const apptHour = new Date(apptTime);
        apptHour.setMinutes(0, 0, 0);
        const apptIdx = hours.reduce((best, h, i) =>
          Math.abs(new Date(h.interval.startTime) - apptHour) <
          Math.abs(new Date(hours[best].interval.startTime) - apptHour) ? i : best, 0);

        const apptSlot = hours[apptIdx];
        const apptRain = apptSlot?.precipitation?.probability?.percent ?? 0;
        const apptTempC = apptSlot?.temperature?.degrees;
        const apptTempF = apptTempC != null ? Math.round(apptTempC * 9 / 5 + 32) : null;
        const apptIcon = conditionToIcon(apptSlot?.weatherCondition?.type);

        // Build day-of timeline in ET (6AM–9PM)
        const apptDateET = apptTime.toLocaleDateString('en-US', { timeZone: 'America/New_York' });
        const apptHourET = parseInt(
          apptTime.toLocaleString('en-US', { timeZone: 'America/New_York', hour: 'numeric', hour12: false })
        );

        const timeline = hours
          .filter(h => {
            const t = new Date(h.interval.startTime);
            const dateET = t.toLocaleDateString('en-US', { timeZone: 'America/New_York' });
            const hourET = parseInt(t.toLocaleString('en-US', { timeZone: 'America/New_York', hour: 'numeric', hour12: false }));
            return dateET === apptDateET && hourET >= 6 && hourET <= 21;
          })
          .map(h => {
            const t = new Date(h.interval.startTime);
            const hourET = parseInt(t.toLocaleString('en-US', { timeZone: 'America/New_York', hour: 'numeric', hour12: false }));
            const rain = h.precipitation?.probability?.percent ?? 0;
            const tempC = h.temperature?.degrees;
            const tempF = tempC != null ? Math.round(tempC * 9 / 5 + 32) : null;
            return {
              hour: hourET,
              label: hourET === 12 ? '12PM' : hourET > 12 ? `${hourET - 12}PM` : `${hourET}AM`,
              icon: conditionToIcon(h.weatherCondition?.type),
              rain,
              tempF,
              isAppt: hourET === apptHourET,
            };
          });

        if (alive) setFc({
          icon: apptIcon,
          tempF: apptTempF,
          rain: apptRain,
          rainAlert: apptRain >= 40,
          timeline,
        });
      } catch (err) {
        console.warn('WeatherForecast skipped:', err.message);
      }
    })();

    return () => { alive = false; };
  }, [scheduledAt, address]);

  if (!fc) return null;

  return (
    <div className={`mt-2 rounded-xl p-3 border ${
      fc.rainAlert ? 'bg-orange-50 border-orange-200' : 'bg-gray-50 border-gray-100'
    }`}>
      <div className="flex items-center gap-2 mb-2.5">
        <span className="text-lg leading-none">{fc.icon}</span>
        {fc.tempF != null && (
          <span className="text-sm font-semibold text-gray-700">{fc.tempF}°F</span>
        )}
        <span className={`text-xs ${fc.rainAlert ? 'text-orange-600 font-semibold' : 'text-gray-500'}`}>
          {fc.rain}% rain at slot
        </span>
        {fc.rainAlert && (
          <span className="ml-auto text-xs font-bold text-orange-700 bg-orange-100 px-2 py-0.5 rounded-full border border-orange-200">
            Rain Likely
          </span>
        )}
      </div>
      {fc.timeline.length > 0 && (
        <div className="flex gap-1 overflow-x-auto pb-0.5">
          {fc.timeline.map(h => (
            <div
              key={h.hour}
              className={`flex-1 min-w-[40px] flex flex-col items-center gap-0.5 py-1.5 px-0.5 rounded-lg ${
                h.isAppt
                  ? 'bg-[#C9A84C]/10 ring-1 ring-[#C9A84C]/40'
                  : 'bg-white border border-gray-100'
              }`}
            >
              <span className="text-[10px] text-gray-400 leading-none">{h.label}</span>
              <span className="text-sm leading-none mt-0.5">{h.icon}</span>
              {h.tempF != null && (
                <span className="text-[10px] text-gray-600 font-medium leading-none">{h.tempF}°</span>
              )}
              <span className={`text-[9px] font-medium leading-none ${
                h.rain >= 40 ? 'text-orange-500' : 'text-blue-400'
              } ${h.rain === 0 ? 'invisible' : ''}`}>
                {h.rain}%
              </span>
            </div>
          ))}
        </div>
      )}
    </div>
  );
}
